import {
  columnOf,
  createTaskColumnSchema,
  taskStatuses,
  type Task,
  type TaskColumn,
  type TaskStatus,
} from '@nexo/types';
import { Button, IconButton, Input, Modal, Popover, Select, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type DragEvent, type ReactNode, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { errorMessage, validate } from '../../lib/forms';
import { can } from '../../lib/permissions';
import { keys, useColumns } from '../../lib/queries';
import { StatusIcon } from './TaskIcons';
import { TaskCard } from './TaskCard';
import { useUpdateTask } from './useTaskMutations';
import { columnName } from './taskUtils';

/**
 * What each column category means (the board's columns are custom states inside them). Spanish
 * keys: translate at render with `t()`.
 */
export const categoryLabels: Record<TaskStatus, string> = {
  todo: 'Por hacer',
  in_progress: 'En curso',
  done: 'Hecho',
};

/** Kanban with the team's columns; cards are dragged between them. */
export function TaskBoard({ tasks, sprintId }: { tasks: Task[]; sprintId: string | null }) {
  const { t } = useTranslation();
  const { team, openTask } = useTeam();
  const columns = useColumns(team.id).data ?? [];
  const update = useUpdateTask();
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<TaskColumn | null>(null);
  const manage = can(team, 'manage_tasks');

  const tasksIn = (column: TaskColumn) =>
    tasks.filter((task) => columnOf(task, columns)?.id === column.id);

  function onDrop(event: DragEvent, column: TaskColumn) {
    event.preventDefault();
    setDragOver(null);
    const id = event.dataTransfer.getData('text/task-id');
    const task = tasks.find((t) => t.id === id);
    if (task && columnOf(task, columns)?.id !== column.id) {
      update.mutate({ id, input: { columnId: column.id } });
    }
  }

  return (
    <div className="scrollbar-thin grid h-full auto-cols-[minmax(17rem,1fr)] grid-flow-col gap-3 overflow-x-auto p-4 sm:p-5">
      {columns.map((column, index) => {
        const list = tasksIn(column);
        const points = list.reduce((sum, t) => sum + (t.storyPoints ?? 0), 0);
        return (
          <section
            key={column.id}
            aria-label={columnName(column.name)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(column.id);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(null);
            }}
            onDrop={(e) => onDrop(e, column)}
            className={cn(
              'flex min-h-0 flex-col rounded-2xl bg-surface-2/70 p-2 ring-1 ring-inset ring-border transition-colors',
              dragOver === column.id && 'bg-accent-soft ring-accent/40',
            )}
          >
            <ColumnHeader
              column={column}
              count={list.length}
              points={points}
              manage={manage}
              canMoveLeft={index > 0}
              canMoveRight={index < columns.length - 1}
              columns={columns}
              onAdd={() => openTask({ mode: 'create', columnId: column.id, sprintId })}
              onDelete={() => setDeleting(column)}
            />
            <div className="scrollbar-thin flex flex-1 flex-col gap-2 overflow-y-auto px-0.5 pb-1">
              {list.map((task) => (
                <div
                  key={task.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/task-id', task.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className="cursor-grab active:cursor-grabbing"
                >
                  <TaskCard task={task} />
                </div>
              ))}
              {list.length === 0 && (
                <p className="rounded-xl border border-dashed border-border-strong/60 px-3 py-8 text-center text-xs text-subtle">
                  {t('Arrastra tareas aquí')}
                </p>
              )}
            </div>
          </section>
        );
      })}
      {manage && columns.length > 0 && <AddColumn />}
      {deleting && (
        <DeleteColumnDialog
          column={deleting}
          columns={columns}
          count={tasksIn(deleting).length}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function useColumnMutation<T>(fn: (input: T) => Promise<unknown>, onDone?: () => void) {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.columns(team.id) });
      void queryClient.invalidateQueries({ queryKey: keys.tasks(team.id) });
      onDone?.();
    },
    onError: (err) =>
      toast({
        title: t('No se pudo cambiar el tablero'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });
}

function ColumnHeader({
  column,
  count,
  points,
  manage,
  canMoveLeft,
  canMoveRight,
  columns,
  onAdd,
  onDelete,
}: {
  column: TaskColumn;
  count: number;
  points: number;
  manage: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  columns: TaskColumn[];
  onAdd: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(column.name);
  const save = useColumnMutation((input: { name?: string; category?: TaskStatus }) =>
    api.columns.update(column.id, input),
  );
  const reorder = useColumnMutation((ids: string[]) => api.columns.reorder(team.id, ids));
  const move = (step: -1 | 1) => {
    const ids = columns.map((c) => c.id);
    const from = ids.indexOf(column.id);
    const to = from + step;
    [ids[from], ids[to]] = [ids[to] ?? column.id, column.id];
    reorder.mutate(ids);
  };

  if (renaming) {
    return (
      <form
        className="flex h-9 items-center gap-1 px-1 pb-1"
        onSubmit={(event) => {
          event.preventDefault();
          const value = name.trim();
          if (value && value !== column.name) save.mutate({ name: value });
          setRenaming(false);
        }}
      >
        <Input
          autoFocus
          aria-label={t('Nombre de la columna')}
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setName(column.name);
              setRenaming(false);
            }
          }}
          className="h-7 text-[13px]"
        />
        <IconButton label={t('Guardar nombre')} size="sm" type="submit">
          <Check />
        </IconButton>
      </form>
    );
  }

  return (
    <header className="group flex h-9 items-center gap-2 px-2 pb-1">
      <StatusIcon status={column.category} />
      <h2 className="truncate text-[13px] font-semibold">{columnName(column.name)}</h2>
      <span className="text-xs text-subtle tabular-nums">{count}</span>
      {points > 0 && (
        <span className="text-xs text-subtle tabular-nums" title={t('Puntos de historia')}>
          · {t('{{points}} pts', { points })}
        </span>
      )}
      <span className="ml-auto flex items-center">
        {column.category !== 'done' && (
          <button
            type="button"
            aria-label={t('Nueva tarea en {{name}}', { name: columnName(column.name) })}
            onClick={onAdd}
            className="rounded-md p-1 text-subtle hover:bg-surface hover:text-text"
          >
            <Plus className="size-4" />
          </button>
        )}
        {manage && (
          <Popover
            className="w-60 p-1"
            trigger={({ toggle, open }) => (
              <button
                type="button"
                aria-label={t('Opciones de {{name}}', { name: columnName(column.name) })}
                aria-expanded={open}
                onClick={toggle}
                className="rounded-md p-1 text-subtle hover:bg-surface hover:text-text"
              >
                <MoreHorizontal className="size-4" />
              </button>
            )}
          >
            {({ close }) => (
              <div role="menu" className="flex flex-col text-sm">
                <MenuItem
                  icon={<Pencil />}
                  onClick={() => {
                    close();
                    setName(column.name);
                    setRenaming(true);
                  }}
                >
                  {t('Renombrar')}
                </MenuItem>
                <p className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('Cuenta como')}
                </p>
                {taskStatuses.map((category) => (
                  <MenuItem
                    key={category}
                    icon={<StatusIcon status={category} />}
                    onClick={() => {
                      close();
                      if (category !== column.category) save.mutate({ category });
                    }}
                  >
                    {t(categoryLabels[category])}
                    {category === column.category && <Check className="ml-auto text-accent" />}
                  </MenuItem>
                ))}
                <div className="my-1 border-t border-border" />
                {canMoveLeft && (
                  <MenuItem
                    icon={<ArrowLeft />}
                    onClick={() => {
                      close();
                      move(-1);
                    }}
                  >
                    {t('Mover a la izquierda')}
                  </MenuItem>
                )}
                {canMoveRight && (
                  <MenuItem
                    icon={<ArrowRight />}
                    onClick={() => {
                      close();
                      move(1);
                    }}
                  >
                    {t('Mover a la derecha')}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<Trash2 />}
                  danger
                  onClick={() => {
                    close();
                    onDelete();
                  }}
                >
                  {t('Borrar columna')}
                </MenuItem>
              </div>
            )}
          </Popover>
        )}
      </span>
    </header>
  );
}

function MenuItem({
  icon,
  danger = false,
  onClick,
  children,
}: {
  icon: ReactNode;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left hover:bg-surface-2 [&_svg]:size-4',
        danger ? 'text-danger' : '[&>svg:first-child]:text-muted',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Last "column": a form to add a custom state. */
function AddColumn() {
  const { t } = useTranslation();
  const { team } = useTeam();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TaskStatus>('in_progress');
  const [error, setError] = useState<string>();
  const create = useColumnMutation(
    (input: { name: string; category: TaskStatus }) => api.columns.create(team.id, input),
    () => {
      setName('');
      setOpen(false);
    },
  );

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(createTaskColumnSchema, { name, category });
    if (result.errors) return setError(result.errors.name);
    create.mutate(result.data);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-11 items-center gap-2 self-start rounded-2xl border border-dashed border-border-strong px-3 text-[13px] text-muted hover:bg-surface-2 hover:text-text"
      >
        <Plus className="size-4" /> {t('Añadir columna')}
      </button>
    );
  }
  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-2 self-start rounded-2xl bg-surface-2/70 p-3 ring-1 ring-inset ring-border"
    >
      <Input
        autoFocus
        aria-label={t('Nombre de la columna')}
        placeholder={t('Ej.: En revisión')}
        maxLength={40}
        value={name}
        aria-invalid={Boolean(error)}
        onChange={(e) => {
          setName(e.target.value);
          setError(undefined);
        }}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
      />
      {error && <p className="text-xs text-danger">{error}</p>}
      <Select
        aria-label={t('Cuenta como')}
        value={category}
        onChange={setCategory}
        options={taskStatuses.map((s) => ({
          value: s,
          label: t('Cuenta como «{{category}}»', { category: t(categoryLabels[s]) }),
          icon: <StatusIcon status={s} />,
        }))}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('Cancelar')}
        </Button>
        <Button size="sm" type="submit" loading={create.isPending}>
          {t('Añadir')}
        </Button>
      </div>
    </form>
  );
}

function DeleteColumnDialog({
  column,
  columns,
  count,
  onClose,
}: {
  column: TaskColumn;
  columns: TaskColumn[];
  count: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const others = columns.filter((c) => c.id !== column.id);
  const [moveTo, setMoveTo] = useState(
    (others.find((c) => c.category === column.category) ?? others[0])?.id ?? '',
  );
  const remove = useColumnMutation(() => api.columns.remove(column.id, moveTo), onClose);
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Borrar «{{name}}»', { name: columnName(column.name) })}
      size="sm"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          {count === 0
            ? t('La columna está vacía.')
            : count === 1
              ? t('Tiene 1 tarea. ¿A qué columna la pasamos?')
              : t('Tiene {{count}} tareas. ¿A qué columna las pasamos?', { count })}
        </p>
        <Select
          aria-label={t('Pasar las tareas a')}
          value={moveTo}
          onChange={setMoveTo}
          options={others.map((c) => ({
            value: c.id,
            label: c.name,
            icon: <StatusIcon status={c.category} />,
          }))}
        />
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => remove.mutate(undefined)}
          >
            {t('Borrar columna')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
